/**
 * Standard multi-therapist day view — the first explicit VIEW-MODEL BUILDER.
 *
 * Architecture rule this file establishes (see
 * docs/calendar-drop-in-replacement-plan.md, Phase 6): the MobiliCalendar
 * engine is view-agnostic — it renders {resources, events, bands} and never
 * learns what a "mode" is. Each view mode (standard day, continuous,
 * single-user date columns, working hours) gets ONE pure builder module like
 * this that maps controller data to engine props. When a new mode arrives it
 * must be a SIBLING module, not a branch inside this one — the legacy
 * CalendarController's standard/continuous interleaving is the anti-pattern
 * this structure exists to avoid.
 *
 * What this builder does today:
 *  - Drops the legacy synthetic patient-availability column (reserved id
 *    "0"). The shared controller still produces it for the FullCalendar
 *    scheduler; the MobiliCalendar surface renders patient availability as a
 *    day-spanning band instead, so selecting a patient can never re-index
 *    the therapist columns. Events targeting the dropped column (PatientSlot
 *    fills) fall out in the engine's index build, which discards events whose
 *    resource is absent.
 *  - Derives the band's blocked ranges from both availability sources.
 */
import {
  PatientAvailabilityDto,
  TimeSlotDto,
} from "../../../api-client";
import {
  CalendarEvent,
  CalendarResource,
} from "../../Calendar/controller/adapters/types";
import { buildPatientBlockedRanges } from "../model/patientAvailability";
import { MCGridConfig, MCTimeRange, PATIENT_RESOURCE_ID } from "../model/types";

export interface StandardDayInput {
  resources: CalendarResource[];
  events: CalendarEvent[];
  date: Date;
  patientAvailabilities: ReadonlyArray<PatientAvailabilityDto> | undefined;
  patientUnavailableTypes: ReadonlyArray<TimeSlotDto> | undefined;
  cfg: Pick<MCGridConfig, "dayStartMin" | "dayEndMin">;
}

export interface StandardDayViewModel {
  resources: CalendarResource[];
  events: CalendarEvent[];
  patientBlockedRanges: MCTimeRange[];
}

export const buildStandardDayViewModel = ({
  resources,
  events,
  date,
  patientAvailabilities,
  patientUnavailableTypes,
  cfg,
}: StandardDayInput): StandardDayViewModel => {
  const therapistResources = resources.filter((r) => r.id !== PATIENT_RESOURCE_ID);
  return {
    // Same array identity when there was no patient column — keeps the
    // engine's memoization unaware anything happened.
    resources:
      therapistResources.length === resources.length ? resources : therapistResources,
    events,
    patientBlockedRanges: buildPatientBlockedRanges({
      availabilities: patientAvailabilities,
      blockingSlots: patientUnavailableTypes,
      date,
      cfg,
    }),
  };
};
